import {Button, Card, Divider, Form, Input, InputNumber, Select, Typography, Row, Col} from "antd";
import {Edit, useDataProvider, useEditContext, useNotify, useRedirect} from "react-admin";
import React, {useState} from "react";
import {ExtendedDataProvider} from "@src/types";
import DeleteButton from "@src/components/Common/DeleteButton"
import ChatCompletionModelForm from "@src/components/LLMModels/LLMForms/ChatCompletionModelForm";
import OpenAIModelForm from "@src/components/LLMModels/LLMForms/OpenAIModelForm";
import GenAIModelForm from "@src/components/LLMModels/LLMForms/GenAIModelForm";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography

const error_mapper = (error: any, form: any, notify: any) => {
    if (error.status == 400) {
        const fields: any[] = [];

        // Handle top-level errors
        for (const [key, value] of Object.entries<any>(error.body)) {
            if (key !== "extra_data") {
                fields.push({ name: key, errors: value });
            }
        }

        // Handle nested extra_data errors
        if (error.body.extra_data) {
            for (const [key, value] of Object.entries<any>(error.body.extra_data)) {
                fields.push({ name: ["extra_data", key], errors: value });
            }
        }

        form.setFields(fields);
    } else
        notify("Something went wrong", "error")
}

const ALLMModelsCreateForm = () => {
    const [form] = Form.useForm ();
    const notify = useNotify ();
    const redirect = useRedirect ();
    const dataProvider: ExtendedDataProvider = useDataProvider ();
    const {record} = useEditContext ();
    const [testingConnectivity, setTestingConnectivity] = useState(false);
    const [connectivityStatus, setConnectivityStatus] = useState<"success" | "error" | null>(null);
    const [selectedProvider, setSelectedProvider] = useState<string | null>(record?.provider || null);

    const resource = "llm_models";
    const title = "LLM Model";

    const transformPayloadEntries = (values: any) => {
        if (values.extra_data?.request_payload_entries) {
            const entries = values.extra_data.request_payload_entries;
            const payload: Record<string, any> = {};
            for (const entry of entries) {
                if (entry?.key) {
                    let val = entry.value;
                    if (val === "true") val = true;
                    else if (val === "false") val = false;
                    else if (val !== "" && !isNaN(Number(val))) val = Number(val);
                    payload[entry.key] = val;
                }
            }
            values.extra_data.request_payload = payload;
            delete values.extra_data.request_payload_entries;
        }
    };

    const onSubmit = (values: any) => {
        transformPayloadEntries(values);

        if (record) {
            dataProvider.update(resource, { id: record.id, data: values, previousData: { id: record.id } })
                .then(() => {
                    notify(`${title} updated successfully`);
                    redirect(`/${resource}`)
                })
                .catch((error: any) => error_mapper(error, form, notify))

        } else {
            dataProvider.create(resource, {data: values})
                .then(() => {
                    notify(`${title} created successfully`);
                    redirect(`/${resource}`)
                })
                .catch((error: any) => error_mapper(error, form, notify))
        }
    }

    const testConnectivity = () => {
        setTestingConnectivity(true);

        form.validateFields().then((values) => {
            transformPayloadEntries(values);

            const verb = record ? `test_connection/${record.id}/` : `test_connection/`;

            dataProvider.createAction(resource, {data: values, verb: verb})
                .then(() => {
                    notify("Connection test successful", "success");
                    setConnectivityStatus("success");
                })
                .catch((error: any) => {
                    const errorMessage = error.body?.error?.error?.message || error.body?.message || error.message || "Unknown error";
                    let statusCode = error.body?.status_code || error.status || "Unknown status";
                    if (statusCode === 500) statusCode = 400;

                    notify(`${errorMessage} (Status: ${statusCode})`, "error");
                    setConnectivityStatus("error");
                })
                .finally(() => setTestingConnectivity(false));
        }).catch(() => {
            notify("Please fill in all required fields before testing connectivity", "warning");
            setTestingConnectivity(false);
        });
    }

    return (
        <Form
            form={form}
            name="create_llm_model"
            labelCol={{ span: 6 }}
            className="row-col"
            style={{marginLeft: "10px", marginRight: "10px"}}
            labelAlign="left"
            initialValues={record ? {
                ...record,
                ...record.extra_data,
                extra_data: {
                    ...record.extra_data,
                    request_payload_entries: record.extra_data?.request_payload
                        ? Object.entries(record.extra_data.request_payload).map(([key, value]) => ({ key, value: String(value) }))
                        : [],
                },
            } : {}}
            onFinish={onSubmit}
            onValuesChange={(changedValues) => {
                setConnectivityStatus(null);
                if (changedValues.provider) {
                    setSelectedProvider(changedValues.provider);
                }
            }}
        >

            <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter a Name' }]}>
                <Input placeholder="Name"/>
            </Form.Item>


            <Form.Item label={"Prompt"} name={"prompt"} rules={[{ required: true }]}>
                <TextArea rows={20} placeholder="Prompt" maxLength={10000} />
            </Form.Item>

            <Form.Item label="Provider" name="provider" rules={[{ required: true, message: "Please select a provider" }]}>
                <Select placeholder="Select provider">
                    <Option value="openai">OpenAI</Option>
                    <Option value="openai_assistant">OpenAI Assistant</Option>
                    <Option value="anthropic">Anthropic</Option>
                    <Option value="mistral">Mistral</Option>
                    <Option value="azure">Azure</Option>
                    <Option value="custom">Custom HTTP</Option>
                    <Option value="xai">xAI</Option>
                    <Option value="gemini">Google Gemini</Option>
                    <Option value="cohere">Cohere</Option>
                    <Option value="genai">Genai</Option>
                </Select>
            </Form.Item>


            <Form.Item
                label="Model Name"
                name="model_name"
                rules={[{ required: true, message: "Please enter the model name" }]}
            >
                <Input placeholder="e.g., gpt-4, claude-3-opus, gemini-pro" />
            </Form.Item>

            <Divider titlePlacement="start">Connection Settings</Divider>

            {selectedProvider === "openai_assistant" && <OpenAIModelForm record={record} />}
            {selectedProvider === "genai" && <GenAIModelForm record={record} />}
            {selectedProvider !== "openai_assistant" && selectedProvider !== "genai" && <ChatCompletionModelForm record={record} />}


            <Form.Item
                label="Timeout (seconds)"
                name="timeout"
                initialValue={60}
                rules={[
                    { required: true, message: "Please enter a timeout" },
                    { type: 'number', min: 1, max: 300, message: "Timeout must be between 1 and 300 seconds" }
                ]}
            >
                <InputNumber placeholder="60" style={{ width: '50%' }} min={1} max={300}/>
            </Form.Item>

            <Divider titlePlacement="start">Cost Settings</Divider>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        label="Input Price"
                        name="input_price_per_1m_tokens"
                        extra="per 1 million input tokens"
                        initialValue={0.0}
                        rules={[
                            { type: 'number', min: 0, message: "Price must be a positive number" }
                        ]}
                    >
                        <InputNumber
                            placeholder="e.g., 0.50"
                            style={{ width: '50%' }}
                            min={0}
                            step={0.01}
                            precision={6}
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label="Output Price"
                        name="output_price_per_1m_tokens"
                        extra="per 1 million output tokens"
                        initialValue={0.0}
                        rules={[
                            { type: 'number', min: 0, message: "Price must be a positive number" }
                        ]}
                    >
                        <InputNumber
                            placeholder="e.g., 1.50"
                            style={{ width: '50%' }}
                            min={0}
                            step={0.01}
                            precision={6}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item>
                <Button style={{ width: "10%" }} type="primary" htmlType="submit" className="form-button">{record? "Update":"Create"}</Button>
                <Button
                    danger={connectivityStatus === "error"}
                    type={connectivityStatus === "success"?"text":"link"}
                    loading={testingConnectivity}
                    onClick={testConnectivity}
                    style={{ width: "12%", marginLeft: "10px", backgroundColor:"white"}}
                >
                    {
                        connectivityStatus === "success" ? <Text type="success">Connection Successful</Text>:
                        connectivityStatus === "error" ? "Connection Failed":
                        "Test Connection"
                    }
                </Button>
                {
                    record &&
                    <DeleteButton title={"LLM Model"} resource={"llm_models"} style={{ width: "10%", float: "right", marginRight: "10px" }} record={record} />
                }
            </Form.Item>
        </Form>

    )
}

const ALLMModelsCreate = (props: any) => {

    return (
        <>
            <Card
                className=" header-solid h-full ant-card pt-0"
                bordered={false}
                title={"LLM Model"}
            >
                {props.id &&
                    <Edit {...props}>
                        <ALLMModelsCreateForm />
                    </Edit>
                }
                {!props.id && <ALLMModelsCreateForm />}
            </Card>
        </>
    )
}

export default ALLMModelsCreate;
